import {
  SPORTSBOOK_HISTORY_HIDDEN_FILL_MAX_REQUESTS,
  SPORTSBOOK_HISTORY_HIDDEN_FILL_MAX_ROWS,
  SPORTSBOOK_HISTORY_PAGE_LIMIT,
} from '../policies/sportsbookHistory.policy.js';

import { createSportsbookHistoryPageToken } from './sportsbookHistoryCursor.cursor.js';

const cursorKey = (cursor) => cursor === null ? '<first>' : cursor;

export const fetchBoundedVisibleSportsbookHistoryPage = async ({
  fetchPage,
  query,
  pageToken = createSportsbookHistoryPageToken(),
  hiddenHistoryIds = new Set(),
  signal,
  visibleLimit = SPORTSBOOK_HISTORY_PAGE_LIMIT,
  maxRequests = SPORTSBOOK_HISTORY_HIDDEN_FILL_MAX_REQUESTS,
  maxRows = SPORTSBOOK_HISTORY_HIDDEN_FILL_MAX_ROWS,
}) => {
  const candidates = [];
  const rowIds = new Set();
  const visitedScanCursors = new Set();
  let scanCursor = pageToken.cursor;
  let canFetch = pageToken.canFetch;
  let requests = 0;
  let scannedRows = 0;
  let snapshotAt = pageToken.snapshotAt;
  let page = { snapshot_at: snapshotAt, next_cursor: scanCursor, has_more: canFetch };

  const collectRow = (row) => {
    scannedRows += 1;
    if (hiddenHistoryIds.has(row.history_id) || rowIds.has(row.history_id)) return;
    rowIds.add(row.history_id);
    candidates.push(row);
  };

  for (const row of pageToken.bufferedRows) {
    if (scannedRows >= maxRows) break;
    collectRow(row);
  }

  while (candidates.length < visibleLimit && canFetch && requests < maxRequests) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    if (scannedRows + visibleLimit > maxRows) break;
    const key = cursorKey(scanCursor);
    if (visitedScanCursors.has(key)) {
      canFetch = false;
      break;
    }
    visitedScanCursors.add(key);

    const result = await fetchPage({
      ...query,
      ...(scanCursor ? { cursor: scanCursor } : {}),
      limit: visibleLimit,
    }, signal);

    requests += 1;
    if (result.rows.length > visibleLimit) throw new Error('history page exceeded the requested limit');
    if (snapshotAt && result.page.snapshot_at !== snapshotAt) throw new Error('history snapshot changed during hidden fill');
    snapshotAt = snapshotAt || result.page.snapshot_at;
    page = result.page;
    for (const row of result.rows) {
      collectRow(row);
    }

    scanCursor = page.next_cursor;
    canFetch = page.has_more === true && typeof scanCursor === 'string' && scanCursor !== '';
    if (canFetch && visitedScanCursors.has(cursorKey(scanCursor))) canFetch = false;
  }

  const rows = candidates.slice(0, visibleLimit);
  const bufferedRows = candidates.slice(visibleLimit);
  const nextPageToken = bufferedRows.length > 0 || canFetch
    ? { cursor: scanCursor, bufferedRows, canFetch, snapshotAt }
    : null;
  return { rows, page, nextPageToken, requests, scannedRows };
};
